export interface IHashtagChild {
  title?: string;
  colour?: string;
}

export interface IHashtag {
  colour?: string;
  title?: string;
  hashtags?: Record<string, IHashtagChild>;
  slug?: keyof IHashtagSections;
  parent?: keyof IHashtagSections;
  style?: React.CSSProperties | null;
}

export interface IHashtagSections {
  diagnosis: IHashtag;
  treatment: IHashtag;
  'work-and-play': IHashtag;
  symptoms: IHashtag;
  'healthy-living': IHashtag;
  research: IHashtag;
  coronavirus: IHashtag;
  global?: IHashtag;
}

export interface IAudiences {
  [key: string]: string[];
}

const hashtagSections: IHashtagSections = {
  diagnosis: {
    colour: '#6fa5a5',
    title: 'Diagnosis',
    hashtags: {
      benign: {
        title: 'Benign',
      },
      disclosure: {
        title: 'Disclosure',
      },
      'limbo-land': {
        title: 'Limbo land',
      },
      mri: {
        title: 'MRI',
      },
      'newly-diagnosed': {
        title: 'Newly diagnosed',
      },
      'primary-progressive': {
        title: 'Primary progressive',
      },
      'relapsing-remitting': {
        title: 'Relapsing remitting',
      },
      'secondary-progressive': {
        title: 'Secondary progressive',
      },
    },
  },
  treatment: {
    colour: '#7EC4A3',
    title: 'Treatment',
    hashtags: {
      aubagio: {
        title: 'Aubagio',
      },
      avonex: {
        title: 'Avonex',
      },
      betaferon: {
        title: 'Betaferon',
      },
      copaxone: {
        title: 'Copaxone',
      },
      extavia: {
        title: 'Extavia',
      },
      gilenya: {
        title: 'Gilenya',
      },
      lemtrada: {
        title: 'Lemtrada',
      },
      ocrevus: {
        title: 'Ocrevus',
      },
      plegridy: {
        title: 'Plegridy',
      },
      rebif: {
        title: 'Rebif',
      },
      tecfidera: {
        title: 'Tecfidera',
      },
      tysabri: {
        title: 'Tysabri',
      },
      unlicensed: {
        title: 'Unlicensed',
      },
      'which-dmt': {
        title: 'Which DMT',
      },
      hsct: {
        title: 'HSCT',
      },
    },
  },
  'work-and-play': {
    colour: '#f27c41',
    title: 'Work and play',
    hashtags: {
      accessibility: {
        title: 'Accessibility',
      },
      'family-and-children': {
        title: 'Family and children',
      },
      'fun-drugs': {
        title: 'Fun drugs',
      },
      'going-out': {
        title: 'Going out',
      },
      'meet-ups': {
        title: 'Meet ups',
      },
      money: {
        title: 'Money',
      },
      'positives-of-ms': {
        title: 'Positives of MS',
      },
      pregnancy: {
        title: 'Pregnancy',
      },
      relationships: {
        title: 'Relationships',
      },
      sex: {
        title: 'Sex',
      },
      travel: {
        title: 'Travel',
      },
      'work-and-study': {
        title: 'Work and study',
      },
    },
  },
  symptoms: {
    colour: '#f98c93',
    title: 'Symptoms',
    hashtags: {
      balance: {
        title: 'Balance',
      },
      'bladder-and-bowel': {
        title: 'Bladder and bowel',
      },
      'blurred-vision': {
        title: 'Blurred vision',
      },
      'brain-fog': {
        title: 'Brain fog',
      },
      'brain-health': {
        title: 'Brain health',
      },
      dizziness: {
        title: 'Dizziness',
      },
      fatigue: {
        title: 'Fatigue',
      },
      'hot-and-cold': {
        title: 'Hot and cold',
      },
      'mental-health': {
        title: 'Mental health',
      },
      numbness: {
        title: 'Numbness',
      },
      'optic-neuritis': {
        title: 'Optic Neuritis',
      },
      pain: {
        title: 'Pain',
      },
      relapses: {
        title: 'Relapses',
      },
      spasticity: {
        title: 'Spasticity',
      },
      steroids: {
        title: 'Steroids',
      },
    },
  },
  'healthy-living': {
    colour: '#b092b0',
    title: 'Healthy living',
    hashtags: {
      diet: {
        title: 'Diet',
      },
      exercise: {
        title: 'Exercise',
      },
      'mind-and-body': {
        title: 'Mind & body',
      },
      'vitamins-and-supplements': {
        title: 'Vitamins and supplements',
      },
    },
  },
  research: {
    colour: '#ffd235',
    title: 'Research',
    hashtags: {
      cause: {
        title: 'Cause',
      },
      'myelin-repair': {
        title: 'Myelin repair',
      },
      repurposing: {
        title: 'Repurposing',
      },
      'stem-cells': {
        title: 'Stem cells',
      },
      trials: {
        title: 'Trials',
      },
    },
  },
  coronavirus: {
    colour: '#ea717b',
    title: 'Coronavirus',
    hashtags: {},
  },
};

export const audiences: IAudiences = {
  'just-diagnosed': [
    'benign',
    'disclosure',
    'newly-diagnosed',
    'relapsing-remitting',
    'resilience',
    'symptoms',
    'treatments',
    'healthy living',
    'work-and-play',
    'research',
  ],
  'those-around-mser': [
    'symptoms',
    'research',
    'work-and-play',
    'diagnosis',
    'disclosure',
  ],
  'im-experienced-with-ms': [
    'research',
    'healthy-living',
    'work-and-play',
    'symptoms',
  ],
  'i-might-have-ms': [
    'diagnosis',
    'mri',
    'limbo-land',
    'symptoms',
    'newly-diagnosed',
  ],
  global: [
    'diagnosis',
    'symptoms',
    'newly-diagnosed',
    'healthy-living',
    'research',
    'disclosure',
    'mri',
  ],
};

export const getHashtag = (
  hashtag: keyof IHashtagSections,
): IHashtag | null => {
  if (Object.prototype.hasOwnProperty.call(hashtagSections, hashtag)) {
    return {
      ...hashtagSections[hashtag],
      slug: hashtag,
      parent: hashtag,
    };
  }
  for (const key in hashtagSections) {
    if (hashtagSections[key as keyof IHashtagSections]?.hashtags?.[hashtag]) {
      return {
        ...hashtagSections[key as keyof IHashtagSections],
        parent: key as keyof IHashtagSections,
        ...hashtagSections[key as keyof IHashtagSections]?.hashtags?.[hashtag],
        slug: hashtag,
      };
    }
  }

  return null;
};

export const getHashtagChild = (hashtag: string): IHashtagChild | null => {
  if (Object.prototype.hasOwnProperty.call(hashtagSections, hashtag)) {
    return {
      title: hashtagSections[hashtag as keyof IHashtagSections]?.title,
    };
  }
  for (const key in hashtagSections) {
    if (hashtagSections[key as keyof IHashtagSections]?.hashtags?.[hashtag]) {
      return {
        title:
          hashtagSections[key as keyof IHashtagSections]?.hashtags?.[hashtag]
            ?.title,
      };
    }
  }
  return null;
};

export const getRelatedHashtags = (query: string): IHashtag => {
  if (query) {
    const slug = query
      .toLowerCase()
      .replace(/ /g, '-') as keyof IHashtagSections;

    const ht = getHashtag(slug);

    if (ht) {
      if (
        ht &&
        ht.parent &&
        hashtagSections[ht.parent as keyof IHashtagSections]?.hashtags
      ) {
        return {
          ...ht,
          hashtags:
            hashtagSections[ht.parent as keyof IHashtagSections]?.hashtags,
        };
      }
    }
  }

  // use global hashtags
  const globalHashtags: Record<string, IHashtagChild> = {};
  for (const key of audiences.global) {
    globalHashtags[key] = getHashtagChild(
      key as keyof IHashtagSections,
    ) as IHashtagChild;
  }
  return {
    slug: 'global',
    parent: 'global',
    colour: '#fff',
    title: 'Hashtags',
    hashtags: globalHashtags,
  };
};

export default hashtagSections;
